import { GenericGetFilterDto, HotelResponseDTO, OccupancyDTO, PaxResponseDto, SaleOrderHotelCardResponseDTO, SaleOrderItemResponseDto, SaleOrderListItemDTO, SaleOrderResponseDto } from '@bandido/shared/dtos';
import { AxiosError, AxiosResponse } from 'axios';
import { useQuery, useMutation } from 'react-query';
import { QueryKeys } from '../common/types';
import { PagedResponse } from '@bandido/shared/dtos';
import { client } from '../common/services';
import { PaxRequestDto } from '@bandido/shared/dtos/clients/get-saleorder.dto';

export interface FlightBookingGetItem {
  id: string;
  paxQty: number;
  destination: string;
  fareProvider: string;
  serviceProvider: string;
  status: string;
  holder: string;
  reservationCode: string;
}

interface GetSaleOrdersForDashboardProps extends GenericGetFilterDto  {
  excludeLinked?: boolean
  onErrorCallback?: (e: AxiosError) => void;
}
export const useGetSaleOrdersForDashboard = (params: GetSaleOrdersForDashboardProps) => {
  const { currentPage = 1, pageSize = 0, search, excludeLinked, onErrorCallback } = params;
  return useQuery({
    queryKey: [QueryKeys.getSaleOrdersForDashboard, currentPage, pageSize, search, excludeLinked],
    queryFn: async (): Promise<PagedResponse<SaleOrderListItemDTO> | undefined> => {
      try {
        const bookings: AxiosResponse<PagedResponse<SaleOrderListItemDTO>> = await client.get('sale-orders/dashboard', {
          params: {
            currentPage,
            pageSize,
            search,
            excludeLinked,
          },
        });

        return bookings.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface GetSaleOrderDetailsProps {
  id?: string;
  onErrorCallback?: (e: AxiosError) => void;
}
export const useGetSaleOrderDetails= (params: GetSaleOrderDetailsProps) => {
  const { id, onErrorCallback } = params;
  return useQuery({
    queryKey: [QueryKeys.getSaleOrdersDetails, id],
    queryFn: async (): Promise<SaleOrderResponseDto | null> => {
      try {
        const result: AxiosResponse<SaleOrderResponseDto> = await client.get(`sale-orders/${id}`);
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        return null;
      }
    },
    refetchOnWindowFocus: true,
    enabled: !!id
  });
};

interface GetSaleOrderDetailItemProps {
  itemId?: string;
  onErrorCallback?: (e: AxiosError) => void;
}
export const useGetSaleOrderDetailItem= (params: GetSaleOrderDetailItemProps) => {
  const { itemId, onErrorCallback } = params;
  return useQuery({
    queryKey: [QueryKeys.getSaleOrdersItems, itemId],
    queryFn: async (): Promise<SaleOrderItemResponseDto & { hotel: SaleOrderHotelCardResponseDTO } | null> => {
      try {
        const result: AxiosResponse<SaleOrderItemResponseDto &
          { hotel: SaleOrderHotelCardResponseDTO }> = await client.get(`sale-orders/item/${itemId}`);
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        return null;
      }
    },
    refetchOnWindowFocus: true,
    enabled: !!itemId
  });
};

interface GetSaleOrderPaxesProps {
  id?: string;
  onErrorCallback?: (e: AxiosError) => void;
}
export const useGetSaleOrderPaxes= (params: GetSaleOrderPaxesProps) => {
  const { id, onErrorCallback } = params;
  return useQuery({
    queryKey: [QueryKeys.getSaleOrdersPaxes, id],
    queryFn: async (): Promise<PaxResponseDto[] | null> => {
      try {
        const result: AxiosResponse<PaxResponseDto[]> = await client.get(`sale-orders/${id}/paxes`);
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        return null;
      }
    },
    refetchOnWindowFocus: true,
    enabled: !!id
  });
};

interface GetCaseSaleOrdersProps {
  caseId?: number;
  onErrorCallback?: (e: AxiosError) => void;
}

export const useGetCaseSaleOrders = (params: GetCaseSaleOrdersProps) => {
  const { caseId, onErrorCallback } = params;
  return useQuery({
    queryKey: [QueryKeys.getCaseSaleOrders, caseId],
    queryFn: async (): Promise<SaleOrderResponseDto[]> => {
      try {
        const result: AxiosResponse<SaleOrderResponseDto[]> = await client.get(`sale-orders/by-case/${caseId}`);
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        return [];
      }
    },
    refetchOnWindowFocus: true,
    enabled: !!caseId
  });
};

interface AddPaxesToSaleOrderProps {
  id: string;
  paxes: PaxRequestDto[];
  onSuccess?: () => void;
  onError?: (e: AxiosError) => void;
}

export const useAddPaxesToSaleOrder = () => {
  return useMutation<void, AxiosError, AddPaxesToSaleOrderProps>({
    mutationFn: async ({ id, paxes }: AddPaxesToSaleOrderProps) => {
      const result = await client.post(`sale-orders/${id}/paxes`, { paxes });
      return result.data;
    },
    onSuccess: (_data: void, { onSuccess }: AddPaxesToSaleOrderProps) => {
      onSuccess?.();
    },
    onError: (error: AxiosError, { onError }: AddPaxesToSaleOrderProps) => {
      onError?.(error);
    },
  });
};

interface UpdateSaleOrderItemOccupanciesProps {
  saleOrderId: string;
  itemId: string;
  occupancies: OccupancyDTO[];
  onSuccess?: () => void;
  onError?: (e: AxiosError) => void;
}

export const useUpdateSaleOrderItemOccupancies = () => {
  return useMutation<void, AxiosError, UpdateSaleOrderItemOccupanciesProps>({
    mutationFn: async ({ saleOrderId, itemId, occupancies }: UpdateSaleOrderItemOccupanciesProps) => {
      const result = await client.put(`sale-orders/${saleOrderId}/item/${itemId}/occupancies`, occupancies);
      return result.data;
    },
    onSuccess: (_data: void, { onSuccess }: UpdateSaleOrderItemOccupanciesProps) => {
      onSuccess?.();
    },
    onError: (error: AxiosError, { onError }: UpdateSaleOrderItemOccupanciesProps) => {
      onError?.(error);
    },
  });
};

//TODO: Refactor when we have improve the cases module.
// (circular references)
interface LinkSaleOrderToCaseProps {
  caseId: number;
  saleOrderId: string;
  onSuccess?: () => void;
  onError?: (e: AxiosError) => void;
}

export const useLinkSaleOrderToCase = () => {
  return useMutation<void, AxiosError, LinkSaleOrderToCaseProps>({
    mutationFn: async ({ caseId, saleOrderId }: LinkSaleOrderToCaseProps) => {
      const result = await client.post(`sale-orders/${saleOrderId}/link-to-case/${caseId}`);
      return result.data;
    },
    onSuccess: (_data: void, variables: LinkSaleOrderToCaseProps) => {
      variables.onSuccess?.();
    },
    onError: (error: AxiosError, variables: LinkSaleOrderToCaseProps) => {
      variables.onError?.(error);
    },
  });
};

interface UnlinkSaleOrderFromCaseProps {
  caseId: number;
  saleOrderId: string;
  onSuccess?: () => void;
  onError?: (e: AxiosError) => void;
}

export const useUnlinkSaleOrderFromCase = () => {
  return useMutation<void, AxiosError, UnlinkSaleOrderFromCaseProps>({
    mutationFn: async ({ caseId, saleOrderId }: UnlinkSaleOrderFromCaseProps) => {
      const result = await client.delete(`sale-orders/${saleOrderId}/unlink-from-case/${caseId}`);
      return result.data;
    },
    onSuccess: (_data: void, variables: UnlinkSaleOrderFromCaseProps) => {
      variables.onSuccess?.();
    },
    onError: (error: AxiosError, variables: UnlinkSaleOrderFromCaseProps) => {
      variables.onError?.(error);
    },
  });
};

interface AddSaleOrderNoteToCaseProps {
  caseId: number;
  saleOrderId: string;
  onSuccess?: () => void;
  onError?: (e: AxiosError) => void;
}

export const useAddSaleOrderNoteToCase = () => {
  return useMutation<void, AxiosError, AddSaleOrderNoteToCaseProps>({
    mutationFn: async ({ caseId, saleOrderId }: AddSaleOrderNoteToCaseProps) => {
      const result = await client.post(`case/${caseId}/sale-order/${saleOrderId}`);
      return result.data;
    },
    onSuccess: (_data: void, variables: AddSaleOrderNoteToCaseProps) => {
      variables.onSuccess?.();
    },
    onError: (error: AxiosError, variables: AddSaleOrderNoteToCaseProps) => {
      variables.onError?.(error);
    }
  });
};

interface RemoveSaleOrderNoteFromCaseProps {
  caseId: number;
  saleOrderId: string;
  onSuccess?: () => void;
  onError?: (e: AxiosError) => void;
}

export const useRemoveSaleOrderNoteFromCase = () => {
  return useMutation<void, AxiosError, RemoveSaleOrderNoteFromCaseProps>({
    mutationFn: async ({ caseId, saleOrderId }: RemoveSaleOrderNoteFromCaseProps) => {
      const result = await client.delete(`case/${caseId}/sale-order/${saleOrderId}`);
      return result.data;
    },
    onSuccess: (_data: void, variables: RemoveSaleOrderNoteFromCaseProps) => {
      variables.onSuccess?.();
    },
    onError: (error: AxiosError, variables: RemoveSaleOrderNoteFromCaseProps) => {
      variables.onError?.(error);
    }
  });
}; 
